import React, { useEffect, useMemo, useState } from "react";
import { Bar, Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import NotificationBell from "../components/NotificationBell";
import "../styles/ManagerDashboard.css";
import { apiUrl } from "../lib/api";

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend);

const fallbackProductImage =
  "https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&w=900&q=80";

const managerSectionTitles = {
  overview: "Store Overview",
  inventory: "Inventory Control",
  pricing: "Pricing Moves",
  schedule: "Team Schedule",
  tasks: "Task Queue",
  reports: "Reports & Trends",
};

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: value >= 1000 ? 0 : 2,
    maximumFractionDigits: value >= 1000 ? 0 : 2,
  }).format(Number(value || 0));

const formatRevenueValue = (value) => {
  const amount = Number(value || 0);

  if (Math.abs(amount) >= 10000) {
    return `$${(amount / 1000).toFixed(1)}K`;
  }

  return formatCurrency(amount);
};

function ManagerIcon({ name, className = "" }) {
  const icons = {
    menu: <path d="M4 7h16M4 12h16M4 17h16" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />,
    overview: <path d="M5.5 10.2 12 5l6.5 5.2V18H14v-4h-4v4H5.5v-7.8Z" fill="none" stroke="currentColor" strokeLinejoin="round" strokeWidth="1.8" />,
    inventory: (
      <>
        <path d="M7 6.2h10l1.3 3.1H5.7L7 6.2Z" fill="none" stroke="currentColor" strokeLinejoin="round" strokeWidth="1.7" />
        <path d="M6.5 9.3h11v8.7H6.5z" fill="none" stroke="currentColor" strokeLinejoin="round" strokeWidth="1.7" />
      </>
    ),
    pricing: (
      <>
        <path d="M12 4.8v14.4M16.1 8.2c0-1.4-1.4-2.4-4-2.4-2.5 0-4 .9-4 2.5 0 3.8 8 1.8 8 5.8 0 1.6-1.4 2.8-4 2.8-2.8 0-4.3-1.1-4.3-2.8" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.7" />
      </>
    ),
    schedule: (
      <>
        <rect x="5" y="6.2" width="14" height="12.8" rx="2.4" fill="none" stroke="currentColor" strokeWidth="1.7" />
        <path d="M8 4.7v3M16 4.7v3M8.5 11.2h7" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.7" />
      </>
    ),
    tasks: (
      <>
        <rect x="6" y="4.5" width="12" height="15" rx="2.2" fill="none" stroke="currentColor" strokeWidth="1.7" />
        <path d="M9 9.2h6M9 12.5h6M9 15.8h4" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.7" />
      </>
    ),
    reports: (
      <>
        <path d="M6 18V9.5M12 18V6M18 18v-4.5" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
        <path d="M4.5 18.2h15" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
      </>
    ),
    profile: (
      <>
        <circle cx="12" cy="9" r="3.4" fill="none" stroke="currentColor" strokeWidth="1.7" />
        <path d="M5.5 18c1.8-2.7 4.2-4 6.5-4s4.7 1.3 6.5 4" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.7" />
      </>
    ),
  };

  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      {icons[name] || icons.overview}
    </svg>
  );
}

const ManagerDashboard = ({ onLogout }) => {
  const [salesData, setSalesData] = useState({ dates: [], revenues: [] });
  const [pricingData, setPricingData] = useState([]);
  const [employeeCount, setEmployeeCount] = useState(0);
  const [inventory, setInventory] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [newItem, setNewItem] = useState({ name: "", description: "", basePrice: "", stockLevel: "", image: "" });
  const [editingItem, setEditingItem] = useState(null);
  const [scheduleData, setScheduleData] = useState({ employeeId: "", shiftStart: "", shiftEnd: "", notes: "" });
  const [activeSection, setActiveSection] = useState("overview");
  const [username] = useState(localStorage.getItem("username") || "Manager");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  const [tasks, setTasks] = useState([]);
  const [inventorySearch, setInventorySearch] = useState("");
  const [savingItemId, setSavingItemId] = useState("");
  const [approvingPriceId, setApprovingPriceId] = useState("");

  const token = localStorage.getItem("token");
  const navigate = useNavigate();
  const managerId = localStorage.getItem("userId");
  const activeSectionTitle = managerSectionTitles[activeSection] || "Store Overview";

  const fetchInventory = async () => {
    try {
      const response = await axios.get(apiUrl("/api/inventory"));
      setInventory(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching inventory:", error);
    }
  };

  const fetchPricing = async () => {
    try {
      const response = await axios.get(apiUrl("/api/pricing/dynamic"));
      setPricingData(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching pricing:", error);
    }
  };

  const refreshManagerData = async () => {
    await Promise.all([fetchInventory(), fetchPricing()]);
  };

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const [salesRes, pricingRes, inventoryRes, employeeCountRes, employeesRes, tasksRes, scheduleRes] = await Promise.all([
          axios.get(apiUrl("/api/sales/analytics")),
          axios.get(apiUrl("/api/pricing/dynamic")),
          axios.get(apiUrl("/api/inventory")),
          axios.get(apiUrl("/api/employees/count")),
          axios.get(apiUrl("/api/employees")),
          axios.get(apiUrl("/api/tasks")),
          axios.get(apiUrl("/api/schedule")),
        ]);

        setSalesData(
          salesRes.data && Array.isArray(salesRes.data.dates) ? salesRes.data : { dates: [], revenues: [] },
        );
        setPricingData(Array.isArray(pricingRes.data) ? pricingRes.data : []);
        setInventory(Array.isArray(inventoryRes.data) ? inventoryRes.data : []);
        setEmployeeCount(employeeCountRes.data.count || 0);
        setEmployees(Array.isArray(employeesRes.data) ? employeesRes.data : []);
        setTasks(Array.isArray(tasksRes.data) ? tasksRes.data : []);
        setSchedules(Array.isArray(scheduleRes.data) ? scheduleRes.data : []);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      }
    };

    fetchDashboardData();
  }, []);

  const handleImageFallback = (event) => {
    if (event.currentTarget.src !== fallbackProductImage) {
      event.currentTarget.src = fallbackProductImage;
    }
  };

  const assignTask = async (event) => {
    event.preventDefault();
    if (!title || !description || !assignedTo) {
      alert("Please fill in all task details.");
      return;
    }

    try {
      await axios.post(apiUrl("/api/tasks"), { title, description, assignedTo });
      const refreshed = await axios.get(apiUrl("/api/tasks"));
      setTasks(Array.isArray(refreshed.data) ? refreshed.data : []);
      setTitle("");
      setDescription("");
      setAssignedTo("");
    } catch (error) {
      console.error("Error assigning task:", error);
    }
  };

  const handleAddItem = async () => {
    if (
      !newItem.name.trim() ||
      !newItem.description.trim() ||
      newItem.basePrice === "" ||
      newItem.stockLevel === "" ||
      !newItem.image.trim()
    ) {
      alert("Please fill out all fields before adding an item.");
      return;
    }

    try {
      await axios.post(
        apiUrl("/api/inventory"),
        {
          name: newItem.name,
          description: newItem.description,
          basePrice: Number(newItem.basePrice),
          stockLevel: Number(newItem.stockLevel),
          image: newItem.image,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      await fetchInventory();
      await refreshManagerData();
      setNewItem({ name: "", description: "", basePrice: "", stockLevel: "", image: "" });
    } catch (error) {
      console.error("Error adding item:", error.response?.data || error.message);
      alert(`Error adding item: ${error.response?.data?.error || "Check required fields"}`);
    }
  };

  const handleUpdateItem = async () => {
    if (!editingItem?._id) return;

    try {
      await axios.put(
        apiUrl(`/api/inventory/${editingItem._id}`),
        {
          name: editingItem.name,
          description: editingItem.description,
          basePrice: Number(editingItem.basePrice),
          stockLevel: Number(editingItem.stockLevel),
          image: editingItem.image,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      await refreshManagerData();
      setEditingItem(null);
    } catch (error) {
      console.error("Error updating item:", error.response?.data || error.message);
    }
  };

  const handleDeleteItem = async (id) => {
    try {
      await axios.delete(apiUrl(`/api/inventory/${id}`), { headers: { Authorization: `Bearer ${token}` } });
      await refreshManagerData();
    } catch (error) {
      console.error("Error deleting item:", error);
    }
  };

  const handleQuickStockUpdate = async (item, nextStockLevel) => {
    const normalizedLevel = Math.max(0, Number(nextStockLevel || 0));
    setSavingItemId(item._id);

    try {
      await axios.put(
        apiUrl(`/api/inventory/${item._id}`),
        {
          name: item.name,
          description: item.description,
          basePrice: Number(item.basePrice || 0),
          stockLevel: normalizedLevel,
          image: item.image,
          brand: item.brand,
          unit: item.unit,
          department: item.department,
          category: item.category,
          highlights: item.highlights,
          images: item.images,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      await refreshManagerData();
    } catch (error) {
      console.error("Error updating stock level:", error.response?.data || error.message);
      alert("Could not update stock right now.");
    } finally {
      setSavingItemId("");
    }
  };

  const handleApprovePrice = async (item) => {
    if (!item?.id || !item?.suggestedPrice) {
      return;
    }

    setApprovingPriceId(item.id);

    try {
      await axios.post(
        apiUrl(`/api/inventory/approve-price/${item.id}`),
        { newPrice: Number(item.suggestedPrice) },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      await refreshManagerData();
    } catch (error) {
      console.error("Error approving price:", error.response?.data || error.message);
      alert("Could not approve this pricing move.");
    } finally {
      setApprovingPriceId("");
    }
  };

  const assignShift = async () => {
    if (!scheduleData.employeeId || !scheduleData.shiftStart || !scheduleData.shiftEnd) {
      alert("Please fill out all required fields.");
      return;
    }

    try {
      await axios.post(apiUrl("/api/schedule"), scheduleData, { headers: { Authorization: `Bearer ${token}` } });
      const refreshedSchedules = await axios.get(apiUrl("/api/schedule"));
      setSchedules(Array.isArray(refreshedSchedules.data) ? refreshedSchedules.data : []);
      setScheduleData({ employeeId: "", shiftStart: "", shiftEnd: "", notes: "" });
      alert("Shift assigned successfully!");
    } catch (error) {
      console.error("Error creating schedule:", error.response?.data || error);
      alert(`Error creating schedule: ${error.response?.data?.error || "Unknown error"}`);
    }
  };

  const logout = () => {
    if (typeof onLogout === "function") {
      onLogout();
      return;
    }

    ["token", "userId", "employeeId", "username", "email", "phone", "address", "role"].forEach((key) =>
      localStorage.removeItem(key),
    );
    navigate("/");
  };

  const inventoryAlerts = useMemo(() => inventory.filter((item) => item.stockLevel < 10), [inventory]);
  const pendingTasks = useMemo(() => tasks.filter((task) => task.status !== "Completed").length, [tasks]);
  const completedTasks = useMemo(() => tasks.filter((task) => task.status === "Completed").length, [tasks]);
  const totalInventoryUnits = useMemo(
    () => inventory.reduce((total, item) => total + (item.stockLevel || 0), 0),
    [inventory],
  );
  const averageStockLevel = useMemo(
    () => (inventory.length ? Math.round(totalInventoryUnits / inventory.length) : 0),
    [inventory, totalInventoryUnits],
  );
  const filteredInventory = useMemo(() => {
    const query = inventorySearch.trim().toLowerCase();
    if (!query) return inventory;

    return inventory.filter((item) =>
      [item.name, item.description]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(query)),
    );
  }, [inventory, inventorySearch]);

  const scheduleRows = useMemo(
    () =>
      schedules
        .flatMap((schedule) =>
          (schedule.shifts || []).map((shift, index) => ({
            ...shift,
            employeeName: schedule.employeeName,
            employeeEmail: schedule.employeeEmail,
            employeeCode: schedule.employeeCode,
            rowId: `${schedule._id}-${index}`,
          })),
        )
        .sort((left, right) => new Date(left.shiftStart) - new Date(right.shiftStart)),
    [schedules],
  );
  const pricingIncreases = useMemo(() => pricingData.filter((item) => item.pricingSignal === "increase").length, [pricingData]);
  const pricingDiscounts = useMemo(() => pricingData.filter((item) => item.pricingSignal === "decrease").length, [pricingData]);
  const lockedPricingItems = useMemo(() => pricingData.filter((item) => item.isManagerLocked), [pricingData]);
  const pendingPricingMoves = useMemo(
    () => pricingData.filter((item) => item.pricingSignal !== "stable" && !item.isManagerLocked),
    [pricingData],
  );
  const featuredPricingMoves = useMemo(
    () =>
      [...pendingPricingMoves]
        .sort((left, right) => Math.abs(right.adjustmentPercent || 0) - Math.abs(left.adjustmentPercent || 0))
        .slice(0, 5),
    [pendingPricingMoves],
  );
  const totalSalesRevenue = useMemo(
    () => salesData.revenues.reduce((total, value) => total + Number(value || 0), 0),
    [salesData],
  );
  const averageRevenuePerDay = useMemo(
    () => (salesData.revenues.length ? totalSalesRevenue / salesData.revenues.length : 0),
    [salesData, totalSalesRevenue],
  );
  const bestRevenueDay = useMemo(() => {
    if (!salesData.revenues.length) return null;
    const highestRevenue = Math.max(...salesData.revenues);
    const index = salesData.revenues.findIndex((value) => Number(value) === highestRevenue);
    return index >= 0
      ? {
          label: new Date(salesData.dates[index]).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          }),
          revenue: highestRevenue,
        }
      : null;
  }, [salesData]);
  const revenueTrendPercent = useMemo(() => {
    const revenues = salesData.revenues || [];
    if (revenues.length < 2) return 0;
    const previous = Number(revenues[revenues.length - 2] || 0);
    const current = Number(revenues[revenues.length - 1] || 0);
    if (!previous) return 0;
    return Math.round(((current - previous) / previous) * 100);
  }, [salesData]);
  const attentionItems = useMemo(() => {
    const stockItems = inventoryAlerts.slice(0, 2).map((item) => ({
      id: `stock-${item._id}`,
      name: item.name,
      note: `${item.stockLevel} left`,
      status: "Low stock alert",
      image: item.image,
      tone: "danger",
    }));

    const pricingItems = featuredPricingMoves
      .filter((item) => item.pricingSignal !== "stable")
      .slice(0, 2)
      .map((item) => ({
        id: `pricing-${item.id || item.name}`,
        name: item.name,
        note: item.priceMessage,
        status: item.pricingSignal === "increase" ? "Price increase suggested" : "Discount suggested",
        image: null,
        tone: item.pricingSignal === "increase" ? "warning" : "success",
      }));

    return [...stockItems, ...pricingItems].slice(0, 4);
  }, [featuredPricingMoves, inventoryAlerts]);
  const highDemandItems = useMemo(
    () =>
      [...inventory]
        .sort((left, right) => Number(right.salesTrend || 0) - Number(left.salesTrend || 0))
        .slice(0, 5),
    [inventory],
  );

  const salesChartData = useMemo(
    () => ({
      labels: salesData.dates,
      datasets: [
        {
          label: "Daily Sales Revenue",
          data: salesData.revenues,
          backgroundColor: "rgba(64, 132, 255, 0.22)",
          borderColor: "rgba(39, 103, 224, 1)",
          tension: 0.35,
          fill: true,
          borderWidth: 3,
          pointRadius: 3,
          pointBackgroundColor: "rgba(39, 103, 224, 1)",
        },
      ],
    }),
    [salesData],
  );

  const pricingChartData = useMemo(
    () => ({
      labels: pricingData.map((item) => item?.name || "Unnamed Product"),
      datasets: [
        {
          label: "Current Price",
          data: pricingData.map((item) => parseFloat(item?.currentPrice) || 0),
          backgroundColor: "rgba(80, 133, 233, 0.7)",
          borderRadius: 12,
        },
        {
          label: "Suggested Price",
          data: pricingData.map((item) => parseFloat(item?.suggestedPrice) || 0),
          backgroundColor: "rgba(68, 205, 174, 0.7)",
          borderRadius: 12,
        },
      ],
    }),
    [pricingData],
  );

  const firstName = username.split(" ")[0] || "Manager";

  const renderOverview = () => (
    <section className="manager-screen manager-screen--overview">
      <section className="manager-greeting">
        <p>Good Morning, {firstName}</p>
        <h1>Main Dashboard</h1>
      </section>

      {inventoryAlerts.length > 0 ? (
        <article className="manager-alert-banner">
          <div className="manager-alert-banner__icon">!</div>
          <div>
            <strong>Urgent restock needed</strong>
            <p>
              Critical inventory levels detected for {inventoryAlerts[0].name}
              {inventoryAlerts.length > 1 ? ` and ${inventoryAlerts.length - 1} more item(s)` : ""}.
            </p>
          </div>
        </article>
      ) : null}

      <article className="manager-card manager-card--hero manager-card--hero-simple">
        <div className="manager-card__header">
          <div>
            <span className="manager-label">Total sales</span>
            <h2>{formatRevenueValue(totalSalesRevenue)}</h2>
          </div>
          <span className="manager-live-dot" />
        </div>

        <div className="manager-hero-summary">
          <span className={`manager-trend ${revenueTrendPercent >= 0 ? "is-positive" : "is-negative"}`}>
            {revenueTrendPercent >= 0 ? "+" : ""}
            {revenueTrendPercent}%
          </span>
          <p>Revenue trend based on the latest sales analytics.</p>
        </div>
      </article>

      <section className="manager-summary-row manager-summary-row--overview">
        <article className="manager-summary-card">
          <span>Active tasks</span>
          <strong>{pendingTasks}</strong>
        </article>
        <article className="manager-summary-card">
          <span>Low stock</span>
          <strong>{inventoryAlerts.length}</strong>
        </article>
        <article className="manager-summary-card">
          <span>Team size</span>
          <strong>{employeeCount}</strong>
        </article>
      </section>

      <section className="manager-action-row">
        <button type="button" className="manager-action-button manager-action-button--primary" onClick={() => setActiveSection("schedule")}>
          Assign Shift
        </button>
        <button type="button" className="manager-action-button" onClick={() => setActiveSection("pricing")}>
          Update Pricing
        </button>
      </section>

      <article className="manager-card">
        <div className="manager-card__header">
          <div>
            <span className="manager-label">Daily revenue</span>
            <h2>Store performance</h2>
          </div>
          <button type="button" className="manager-link-button" onClick={() => setActiveSection("reports")}>
            Details
          </button>
        </div>
        <div className="manager-chart-wrap manager-chart-wrap--compact">
          <Line data={salesChartData} options={{ maintainAspectRatio: false, plugins: { legend: { display: false } } }} />
        </div>
      </article>

      <article className="manager-card">
        <div className="manager-card__header">
          <div>
            <span className="manager-label">Attention Required</span>
            <h2>What needs attention now</h2>
          </div>
        </div>

        <div className="manager-attention-list">
          {attentionItems.length ? (
            attentionItems.map((item) => (
              <article key={item.id} className={`manager-attention-item manager-attention-item--${item.tone}`}>
                {item.image ? (
                  <img src={item.image || fallbackProductImage} alt={item.name} onError={handleImageFallback} />
                ) : (
                  <div className="manager-attention-item__icon">{item.name?.charAt(0) || "!"}</div>
                )}
                <div>
                  <strong>{item.name}</strong>
                  <p>{item.status}</p>
                </div>
                <span>{item.note}</span>
              </article>
            ))
          ) : (
            <div className="manager-empty-state">
              <strong>No urgent stock issues.</strong>
              <p>The shelves look stable right now.</p>
            </div>
          )}
        </div>
      </article>
    </section>
  );

  const renderInventory = () => (
    <section className="manager-screen">
      <section className="manager-summary-row">
        <article className="manager-summary-card">
          <span>Catalog items</span>
          <strong>{inventory.length}</strong>
        </article>
        <article className="manager-summary-card">
          <span>Low stock</span>
          <strong>{inventoryAlerts.length}</strong>
        </article>
        <article className="manager-summary-card">
          <span>Avg stock</span>
          <strong>{averageStockLevel}</strong>
        </article>
      </section>

      <section className="manager-detail-grid manager-detail-grid--inventory">
        <article className="manager-card manager-card--inventory-form">
          <div className="manager-card__header">
            <div>
              <span className="manager-label">Inventory Management</span>
              <h2>Add product</h2>
            </div>
          </div>

          <p className="manager-muted-copy">
            Keep the catalog clean here, then use the quick stock controls on the right for day-to-day shelf updates.
          </p>

          <div className="manager-form-grid">
            <input placeholder="Name" value={newItem.name} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })} />
            <input
              type="number"
              placeholder="Base Price"
              value={newItem.basePrice}
              onChange={(e) => setNewItem({ ...newItem, basePrice: e.target.value })}
            />
            <input
              type="number"
              placeholder="Stock Level"
              value={newItem.stockLevel}
              onChange={(e) => setNewItem({ ...newItem, stockLevel: e.target.value })}
            />
            <textarea
              className="manager-form-grid__full"
              placeholder="Description"
              value={newItem.description}
              onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
            />
            <input
              className="manager-form-grid__full"
              type="text"
              placeholder="Image URL"
              value={newItem.image}
              onChange={(e) => setNewItem({ ...newItem, image: e.target.value })}
            />
          </div>
          <button className="manager-primary-button" onClick={handleAddItem}>
            Add item
          </button>

          <div className="manager-inventory-tips">
            <article>
              <strong>Fast stock updates</strong>
              <p>Use the plus and minus controls on each item card to keep counts accurate without opening the edit form.</p>
            </article>
            <article>
              <strong>Better search</strong>
              <p>Search by product name or description to jump straight to the item you want to update.</p>
            </article>
          </div>
        </article>

        <article className="manager-card manager-card--inventory-catalog">
          <div className="manager-card__header">
            <div>
              <span className="manager-label">Inventory Search</span>
              <h2>Current catalog</h2>
            </div>
            <span className="manager-pill">{inventory.length} items</span>
          </div>

          <div className="manager-search">
            <input
              type="text"
              placeholder="Search inventory..."
              value={inventorySearch}
              onChange={(event) => setInventorySearch(event.target.value)}
            />
          </div>

          <div className="manager-inventory-list">
            {filteredInventory.length ? (
              filteredInventory.map((item) => (
                <article className="manager-inventory-card" key={item._id}>
                  <div className="manager-inventory-card__content">
                    <div className="manager-inventory-card__main">
                      <img src={item.image || fallbackProductImage} alt={item.name} onError={handleImageFallback} />
                      <div className="manager-inventory-card__copy">
                        <div className="manager-inventory-card__title">
                          <strong>{item.name}</strong>
                          <span className="manager-pill">{item.category || item.department || "Catalog item"}</span>
                        </div>
                        <p>{item.description}</p>
                      </div>
                    </div>

                    <div className="manager-inventory-card__metrics">
                      <article className="manager-inventory-metric">
                        <span>Live price</span>
                        <strong>{formatCurrency(Number(item.basePrice || 0))}</strong>
                        {Number(item.compareAtPrice || 0) > Number(item.basePrice || 0) ? (
                          <small>Was {formatCurrency(Number(item.compareAtPrice || 0))}</small>
                        ) : (
                          <small>Regular shelf price</small>
                        )}
                      </article>
                      <article className="manager-inventory-metric">
                        <span>Current stock</span>
                        <strong>{item.stockLevel}</strong>
                        <small>{item.stockLevel <= 10 ? "Restock soon" : "Healthy level"}</small>
                      </article>
                    </div>
                  </div>

                  <div className="manager-inventory-card__rail">
                    <div className="manager-stock-control">
                      <span>Stock</span>
                      <div className="manager-stock-control__actions">
                        <button
                          type="button"
                          className="manager-stock-button"
                          onClick={() => handleQuickStockUpdate(item, Number(item.stockLevel || 0) - 1)}
                          disabled={savingItemId === item._id}
                        >
                          -
                        </button>
                        <input
                          type="number"
                          min="0"
                          value={item.stockLevel ?? 0}
                          onChange={(event) =>
                            setInventory((current) =>
                              current.map((entry) =>
                                entry._id === item._id ? { ...entry, stockLevel: event.target.value } : entry,
                              ),
                            )
                          }
                          onBlur={(event) => handleQuickStockUpdate(item, event.target.value)}
                        />
                        <button
                          type="button"
                          className="manager-stock-button"
                          onClick={() => handleQuickStockUpdate(item, Number(item.stockLevel || 0) + 1)}
                          disabled={savingItemId === item._id}
                        >
                          +
                        </button>
                      </div>
                    </div>

                    <div className="manager-inline-actions manager-inline-actions--inventory">
                      <button className="manager-primary-button" onClick={() => setEditingItem({ ...item, basePrice: item.basePrice || 0 })}>
                        Edit
                      </button>
                      <button className="manager-secondary-button" onClick={() => handleDeleteItem(item._id)}>
                        Delete
                      </button>
                    </div>
                  </div>
                </article>
              ))
            ) : (
              <div className="manager-empty-state">
                <strong>No products match that search.</strong>
                <p>Try a different product name or clear the filter.</p>
              </div>
            )}
          </div>
        </article>
      </section>

      {editingItem && (
        <article className="manager-card">
          <div className="manager-card__header">
            <div>
              <span className="manager-label">Edit Inventory Item</span>
              <h2>Update item details</h2>
            </div>
          </div>

          <div className="manager-form-grid">
            <input placeholder="Name" value={editingItem.name} onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })} />
            <input
              placeholder="Description"
              value={editingItem.description}
              onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })}
            />
            <input
              type="number"
              placeholder="Base Price"
              value={editingItem.basePrice || 0}
              onChange={(e) => setEditingItem({ ...editingItem, basePrice: e.target.value })}
            />
            <input
              type="number"
              placeholder="Stock Level"
              value={editingItem.stockLevel ?? ""}
              onChange={(e) => setEditingItem({ ...editingItem, stockLevel: e.target.value })}
            />
            <input
              className="manager-form-grid__full"
              type="text"
              placeholder="Image URL"
              value={editingItem.image}
              onChange={(e) => setEditingItem({ ...editingItem, image: e.target.value })}
            />
          </div>

          <div className="manager-inline-actions">
            <button className="manager-primary-button" onClick={handleUpdateItem}>
              Update item
            </button>
            <button className="manager-secondary-button" onClick={() => setEditingItem(null)}>
              Cancel
            </button>
          </div>
        </article>
      )}
    </section>
  );

  const renderSchedule = () => (
    <section className="manager-screen">
      <section className="manager-summary-row">
        <article className="manager-summary-card">
          <span>Scheduled employees</span>
          <strong>{schedules.length}</strong>
        </article>
        <article className="manager-summary-card">
          <span>Upcoming shifts</span>
          <strong>{scheduleRows.length}</strong>
        </article>
      </section>

      <section className="manager-detail-grid">
        <article className="manager-card">
          <div className="manager-card__header">
            <div>
              <span className="manager-label">Assign Shift</span>
              <h2>Build the staff schedule</h2>
            </div>
          </div>

          <div className="manager-form-grid">
            <select value={scheduleData.employeeId} onChange={(e) => setScheduleData({ ...scheduleData, employeeId: e.target.value })}>
              <option value="">Select Employee</option>
              {employees.map((employee) => (
                <option key={employee._id} value={employee._id}>
                  {employee.username}
                </option>
              ))}
            </select>
            <input
              type="datetime-local"
              value={scheduleData.shiftStart}
              onChange={(e) => setScheduleData({ ...scheduleData, shiftStart: e.target.value })}
            />
            <input
              type="datetime-local"
              value={scheduleData.shiftEnd}
              onChange={(e) => setScheduleData({ ...scheduleData, shiftEnd: e.target.value })}
            />
            <input
              type="text"
              placeholder="Notes (Optional)"
              value={scheduleData.notes}
              onChange={(e) => setScheduleData({ ...scheduleData, notes: e.target.value })}
            />
          </div>
          <button className="manager-primary-button" onClick={assignShift}>
            Assign shift
          </button>
        </article>

        <article className="manager-card">
          <div className="manager-card__header">
            <div>
              <span className="manager-label">Assigned Shifts</span>
              <h2>Upcoming coverage</h2>
            </div>
          </div>

          <div className="manager-task-list">
            {scheduleRows.length ? (
              scheduleRows.slice(0, 6).map((shift) => (
                <article key={shift.rowId} className="manager-task-row manager-task-row--schedule">
                  <div>
                    <strong>{shift.employeeName || "Employee"}</strong>
                    <p>
                      {new Date(shift.shiftStart).toLocaleString("en-US", {
                        month: "short",
                        day: "numeric",
                        hour: "numeric",
                        minute: "2-digit",
                      })}
                      {" - "}
                      {new Date(shift.shiftEnd).toLocaleString("en-US", {
                        hour: "numeric",
                        minute: "2-digit",
                      })}
                    </p>
                  </div>
                  <span>{shift.notes || shift.employeeCode || "Shift assigned"}</span>
                </article>
              ))
            ) : (
              <div className="manager-empty-state">
                <strong>No shifts assigned yet.</strong>
                <p>Create a new shift and it will appear here right away.</p>
              </div>
            )}
          </div>
        </article>
      </section>
    </section>
  );

  const renderTasks = () => (
    <section className="manager-screen">
      <section className="manager-summary-row">
        <article className="manager-summary-card">
          <span>Total tasks</span>
          <strong>{tasks.length}</strong>
        </article>
        <article className="manager-summary-card">
          <span>Pending</span>
          <strong>{pendingTasks}</strong>
        </article>
        <article className="manager-summary-card">
          <span>Completed</span>
          <strong>{completedTasks}</strong>
        </article>
      </section>

      <section className="manager-detail-grid">
        <article className="manager-card">
          <div className="manager-card__header">
            <div>
              <span className="manager-label">Assign Task</span>
              <h2>Create employee work</h2>
            </div>
          </div>

          <form className="manager-form-grid manager-form-grid--stacked" onSubmit={assignTask}>
            <input type="text" placeholder="Task Title" value={title} onChange={(e) => setTitle(e.target.value)} />
            <textarea placeholder="Task Description" value={description} onChange={(e) => setDescription(e.target.value)} />
            <select value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)}>
              <option value="">Select Employee</option>
              {employees.map((employee) => (
                <option key={employee._id} value={employee._id}>
                  {employee.username}
                </option>
              ))}
            </select>
            <button className="manager-primary-button" type="submit">
              Assign task
            </button>
          </form>
        </article>

        <article className="manager-card">
          <div className="manager-card__header">
            <div>
              <span className="manager-label">Assigned Tasks</span>
              <h2>Task board</h2>
            </div>
            <span className="manager-pill">{tasks.length} total</span>
          </div>

          <div className="manager-task-list">
            {tasks.length ? (
              tasks.map((task) => (
                <article key={task._id} className="manager-task-row">
                  <div>
                    <strong>{task.title}</strong>
                    <p>{task.description}</p>
                    <small className="manager-task-assignee">
                      Assigned to {task.assignedTo?.username || task.assignedTo?.employeeId || "Employee"}
                    </small>
                  </div>
                  <span className={`manager-task-status manager-task-status--${task.status.toLowerCase()}`}>
                    {task.status}
                  </span>
                </article>
              ))
            ) : (
              <div className="manager-empty-state">
                <strong>No assigned tasks.</strong>
                <p>Create a task to start the workflow.</p>
              </div>
            )}
          </div>
        </article>
      </section>
    </section>
  );

  const renderReports = () => (
    <section className="manager-screen">
      <section className="manager-summary-row">
        <article className="manager-summary-card">
          <span>Total revenue</span>
          <strong>{formatRevenueValue(totalSalesRevenue)}</strong>
        </article>
        <article className="manager-summary-card">
          <span>Average per day</span>
          <strong>{formatRevenueValue(averageRevenuePerDay)}</strong>
        </article>
        <article className="manager-summary-card">
          <span>Approved prices</span>
          <strong>{lockedPricingItems.length}</strong>
        </article>
      </section>

      <article className="manager-card">
        <div className="manager-card__header">
          <div>
            <span className="manager-label">Reports</span>
            <h2>Sales analysis</h2>
          </div>
        </div>

        <div className="manager-chart-wrap">
          <Line data={salesChartData} options={{ maintainAspectRatio: false }} />
        </div>
      </article>

      <section className="manager-detail-grid">
        <article className="manager-card">
          <div className="manager-card__header">
            <div>
              <span className="manager-label">Top Revenue Moment</span>
              <h2>Best performing day</h2>
            </div>
          </div>

          {bestRevenueDay ? (
            <div className="manager-report-highlight">
              <strong>{bestRevenueDay.label}</strong>
              <p>{formatRevenueValue(bestRevenueDay.revenue)} in sales across the current analytics window.</p>
            </div>
          ) : (
            <div className="manager-empty-state">
              <strong>No revenue trend available.</strong>
              <p>Add more sales activity to unlock reporting insights.</p>
            </div>
          )}
        </article>

        <article className="manager-card">
          <div className="manager-card__header">
            <div>
              <span className="manager-label">Demand Watch</span>
              <h2>Top-moving products</h2>
            </div>
          </div>

          <div className="manager-task-list">
            {highDemandItems.map((item) => (
              <article key={item._id} className="manager-task-row manager-task-row--pricing">
                <div>
                  <strong>{item.name}</strong>
                  <p>{item.category || item.department || "Inventory item"}</p>
                </div>
                <span className="manager-task-status manager-task-status--increase">{item.salesTrend || 0} trend</span>
              </article>
            ))}
          </div>
        </article>
      </section>
    </section>
  );

  const sections = [
    ["overview", "Overview"],
    ["inventory", "Inventory"],
    ["pricing", "Pricing"],
    ["schedule", "Schedule"],
    ["tasks", "Tasks"],
    ["reports", "Reports"],
  ];

  return (
    <div className="manager-dashboard">
      <header className="manager-topbar">
        <div className="manager-topbar__brand">
          <button type="button" className="manager-icon-button" aria-label="Open menu">
            <ManagerIcon name="menu" />
          </button>
          <div className="manager-topbar__badge">IG</div>
          <div>
            <span>IntelliGrocer</span>
            <strong>{activeSectionTitle}</strong>
          </div>
        </div>

        <div className="manager-topbar__actions">
          <NotificationBell userId={managerId} />
          <button type="button" className="manager-logout-button" onClick={logout}>
            Logout
          </button>
          <button type="button" className="manager-avatar-button" onClick={() => setActiveSection("overview")}>
            {(username || "M").charAt(0).toUpperCase()}
          </button>
        </div>
      </header>

      <main className="manager-main">
        <section className="manager-top-tabs">
          {sections.map(([key, label]) => (
            <button key={key} type="button" className={activeSection === key ? "is-active" : ""} onClick={() => setActiveSection(key)}>
              <ManagerIcon
                name={
                  key === "overview"
                    ? "overview"
                    : key === "inventory"
                      ? "inventory"
                      : key === "pricing"
                        ? "pricing"
                        : key === "schedule"
                          ? "schedule"
                          : key === "tasks"
                            ? "tasks"
                            : "reports"
                }
              />
              <span>{label}</span>
            </button>
          ))}
        </section>

        {activeSection === "overview" && renderOverview()}
        {activeSection === "inventory" && renderInventory()}
        {activeSection === "pricing" && (
          <section className="manager-screen">
            <section className="manager-summary-row">
              <article className="manager-summary-card">
                <span>Price increases</span>
                <strong>{pricingIncreases}</strong>
              </article>
              <article className="manager-summary-card">
                <span>Discounts</span>
                <strong>{pricingDiscounts}</strong>
              </article>
              <article className="manager-summary-card">
                <span>Tracked products</span>
                <strong>{pricingData.length}</strong>
              </article>
            </section>

            <article className="manager-card">
              <div className="manager-card__header">
                <div>
                  <span className="manager-label">Dynamic Pricing</span>
                  <h2>Pricing overview</h2>
                </div>
              </div>
              <div className="manager-chart-wrap">
                {pricingData.length ? (
                  <Bar data={pricingChartData} options={{ maintainAspectRatio: false }} />
                ) : (
                  <div className="manager-empty-state">
                    <strong>No pricing data available.</strong>
                    <p>Pricing recommendations will appear here when available.</p>
                  </div>
                )}
              </div>
            </article>

            <article className="manager-card">
              <div className="manager-card__header">
                <div>
                  <span className="manager-label">Pricing Notes</span>
                  <h2>Recommended moves to approve</h2>
                </div>
              </div>

              <div className="manager-task-list">
                {featuredPricingMoves.length ? (
                  featuredPricingMoves.map((item) => (
                    <article key={item.id || item.name} className="manager-task-row manager-task-row--pricing">
                      <div>
                        <strong>{item.name}</strong>
                        <p>{item.priceMessage}</p>
                        <small className="manager-pricing-copy">
                          ${Number(item.currentPrice || 0).toFixed(2)} to ${Number(item.suggestedPrice || 0).toFixed(2)}
                        </small>
                      </div>
                      <div className="manager-pricing-actions">
                        <span className={`manager-task-status manager-task-status--${item.pricingSignal}`}>
                          {item.adjustmentPercent > 0 ? `+${item.adjustmentPercent}%` : `${item.adjustmentPercent}%`}
                        </span>
                        {item.pricingSignal !== "stable" ? (
                          <button
                            type="button"
                            className="manager-secondary-button manager-secondary-button--approve"
                            onClick={() => handleApprovePrice(item)}
                            disabled={approvingPriceId === item.id}
                          >
                            {approvingPriceId === item.id ? "Approving..." : "Approve"}
                          </button>
                        ) : null}
                      </div>
                    </article>
                  ))
                ) : (
                  <div className="manager-empty-state">
                    <strong>No pricing changes waiting for approval.</strong>
                    <p>The current catalog is either stable or already running on manager-approved pricing.</p>
                  </div>
                )}
              </div>
            </article>

            <article className="manager-card">
              <div className="manager-card__header">
                <div>
                  <span className="manager-label">Approved Pricing</span>
                  <h2>Manager-approved price active</h2>
                </div>
              </div>

              <div className="manager-task-list">
                {lockedPricingItems.length ? (
                  lockedPricingItems.slice(0, 5).map((item) => (
                    <article key={`locked-${item.id || item.name}`} className="manager-task-row manager-task-row--pricing">
                      <div>
                        <strong>{item.name}</strong>
                        <p>{item.priceMessage}</p>
                        <small className="manager-pricing-copy">
                          Live at {formatCurrency(Number(item.currentPrice || 0))}
                        </small>
                      </div>
                      <span className="manager-task-status manager-task-status--locked">Active</span>
                    </article>
                  ))
                ) : (
                  <div className="manager-empty-state">
                    <strong>No manager-approved pricing is active.</strong>
                    <p>Approved discounts and price changes will appear here while the lock is in effect.</p>
                  </div>
                )}
              </div>
            </article>
          </section>
        )}
        {activeSection === "schedule" && renderSchedule()}
        {activeSection === "tasks" && renderTasks()}
        {activeSection === "reports" && renderReports()}
      </main>

      <nav className="manager-bottom-nav">
        {sections.map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={activeSection === key ? "is-active" : ""}
            onClick={() => setActiveSection(key)}
          >
            <ManagerIcon
              name={
                key === "overview"
                  ? "overview"
                  : key === "inventory"
                    ? "inventory"
                    : key === "pricing"
                      ? "pricing"
                      : key === "schedule"
                        ? "schedule"
                        : key === "tasks"
                          ? "tasks"
                          : "reports"
              }
            />
            <span>{label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
};

export default ManagerDashboard;
